import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContact, selectContact, updateContact } from '@repo/redux/actions/contacts';
import Container from '@components/ContentContainer';
import Actions, { profileIsEmpty } from './Actions';
import { ContactHeader } from '@repo/components/headers';

import {
  ContactBio,
  ContactEducation,
  ContactExperience,
  ContactInterests,
  ContactSocialProfiles,
  ContactWebsites,
} from '@repo/components/contact';

import { contactNotesSelector } from '@repo/redux/actions/notes';
import {
  creatingNewNoteInContactViewSelector,
  selectPageSource,
  setActiveContact,
} from '@repo/redux/actions/ui';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAutoFocus, useScrollingClass, useHotkeyHandler, useAuthContext } from '@repo/hooks';
import analytics from '@repo/utils/helpers/analytics';
import { fetchContactShareStatus } from '@repo/redux/actions/shareStatuses/contacts';
import styles from './Contact.module.scss';
import EmptyContact, { RestrictedContact } from './EmptyContact';
import MissingContact from './MissingContact';
import { ContactViewSidebarDisplay } from '@components/Contact/Sidebar';
import Similar from '@components/Contact/Similar';
import Contact, { RelatedContact } from '@repo/utils/models/Contact';
import { TabNavigation } from '@repo/components/menus';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import Timeline from '@components/Contact/Timeline';
import TimelineFilterSetter, {
  defaultTimelineFilterOption,
} from '@components/Contact/Timeline/Filter';
import Highlights from '@components/Contact/Highlights';
import { TimelineFilter } from '@repo/utils/models/Timeline';
import { selectContactInteractionsSummary } from '@repo/redux/reducers/data/moments';

function About({ contact, isEmptyProfile }: { contact: Contact; isEmptyProfile: boolean }) {
  if (isEmptyProfile) {
    return (
      <div>
        <EmptyContact />
      </div>
    );
  }

  return (
    <>
      <ContactBio contact={contact} />
      <ContactSocialProfiles contact={contact} />
      {contact.isPersisted && <Highlights contactID={contact.id} />}
      <ContactExperience contact={contact} />
      <ContactEducation contact={contact} />
      <ContactWebsites contact={contact} />
      <Similar contact={contact} />
      <ContactInterests contact={contact} />
    </>
  );
}

function ProfileTabs({
  contact,
  isSelf,
  isEmptyProfile,
  setRelatedContacts,
}: {
  contact: Contact;
  isSelf: boolean;
  isEmptyProfile: boolean;
  setRelatedContacts: (relatedContacts: RelatedContact[]) => void;
}) {
  const creatingNewNote = useSelector(creatingNewNoteInContactViewSelector);
  const [activeTabIdx, setActiveTabIdx] = useState(isSelf ? 1 : 0);
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>(null);

  const tabs = useMemo(() => {
    if (isSelf) {
      return [{ index: 1, title: 'About' }];
    }

    return [
      { index: 0, title: 'Timeline' },
      { index: 1, title: 'About' },
    ];
  }, [isSelf]);

  useEffect(() => {
    if (creatingNewNote && activeTabIdx != 0) {
      setActiveTabIdx(0);
    }

    if (creatingNewNote && timelineFilter !== null) {
      setTimelineFilter(defaultTimelineFilterOption.value);
    }
  }, [creatingNewNote]);

  return (
    <>
      <TabNavigation tabs={tabs} activeTabIdx={activeTabIdx} setActiveTabIdx={setActiveTabIdx}>
        {activeTabIdx === 0 && (
          <TimelineFilterSetter
            contactID={contact.id}
            timelineFilter={timelineFilter}
            setTimelineFilter={setTimelineFilter}
          />
        )}
      </TabNavigation>
      <StateBasedFadeTransition
        state={String(activeTabIdx)}
        elementByStateMap={{
          '0': (
            <Timeline
              contact={contact}
              relatedContacts={contact.related}
              setRelatedContacts={setRelatedContacts}
              filterValue={timelineFilter}
            />
          ),
          '1': <About contact={contact} isEmptyProfile={isEmptyProfile} />,
        }}
        duration="fast"
      />
    </>
  );
}

export default function ContactView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, contactIsRestricted, getEntitlementValue } = useAuthContext();
  const location = useLocation();
  const { id: contactID } = useParams();
  // Even though we have contactID, the contact selector will return a blank contact
  // with a temp ID on the first frame before the contact is fetched. Now that we're passing
  // the contact down as a prop in a few places, this ensures that the correct ID is picked
  // up by any children that need to fetch/select using the ID.
  const blankContact = useMemo(
    () => contactID && new Contact({ id: String(contactID) }),
    [contactID],
  );

  const isSelf = user?.contact_id === Number(contactID);
  const contact = useSelector(selectContact(contactID, blankContact));
  const notes = useSelector(contactNotesSelector(contactID));
  const pageSource = useSelector(selectPageSource);
  const summary = useSelector(selectContactInteractionsSummary(contactID));
  const containerRef = useRef<HTMLDivElement>(null);
  const isEmptyProfile = useMemo(() => profileIsEmpty(contact), [contact, notes.length]);

  const setRelatedContacts = (related) => dispatch(updateContact(contact.copyWith({ related })));

  useScrollingClass(containerRef, 'contact-scrolled');
  useAutoFocus(containerRef);

  useEffect(() => {
    const source = location.state?.source || pageSource;
    if (source) {
      analytics.track('Contact Viewed', { contactId: contactID, source });
    }

    dispatch(fetchContact({ id: contactID }));
    dispatch(fetchContactShareStatus(contactID));
  }, [contactID]);

  useEffect(() => {
    // We don't want to set the selected contact until it is saved. This handles a bug
    // when trying to set selectedContact when creating a new contact.
    if (contact.isPersisted || contact.displayName) {
      dispatch(setActiveContact(contact, 'contact-profile'));
    }

    return () => {
      dispatch(setActiveContact(null));
    };
  }, [contactID, contact.updated, contact.displayName]);

  useHotkeyHandler(
    {
      handler: (e) => {
        if (
          e.key?.toLowerCase() == 'e' &&
          !contactIsRestricted(contact) &&
          !getEntitlementValue('contacts.edit.disabled') &&
          contact.isLoaded &&
          !contact.error
        ) {
          e.preventDefault();
          navigate(`/contact/${contactID}/form`);
        }
      },
      priority: 1,
    },
    [contact, navigate],
  );

  const renderMainPanel = () => {
    if (contactIsRestricted(contact) && !isSelf) {
      return <RestrictedContact contact={contact} />;
    }

    return (
      <ProfileTabs
        contact={contact}
        isSelf={isSelf}
        isEmptyProfile={isEmptyProfile}
        setRelatedContacts={setRelatedContacts}
      />
    );
  };

  return (
    <Container key={contactID}>
      <Helmet title={contact.displayName} />
      <Container.Primary
        ref={containerRef}
        className={styles.container}
        header={contact.error ? <></> : <Actions isSelf={isSelf} contact={contact} />}
      >
        {contact.error ? (
          <MissingContact />
        ) : (
          <div className={styles.content}>
            <ContactHeader className={styles.header} contact={contact} />
            {renderMainPanel()}
          </div>
        )}
      </Container.Primary>
      <ContactViewSidebarDisplay contact={contact} summary={!isSelf && summary} />
    </Container>
  );
}
